import { DEFAULT_FILTER } from './user';
import { AppUser } from 'Src/views/App/App';
import axios from 'Src/helpers/api/axiosInstance';
import { DEFAULT_LIMIT, DEFAULT_SKIP } from 'Src/consts/table';

const AWARD_TYPES_PAGE_LIMIT = 100;
const GATEWAY_RETRY_DELAY = 300;

function isRetryableGatewayError(error): boolean {
  const status = error && error.response && error.response.status;
  return status === 502 || status === 503 || status === 504;
}

function wait(delay: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, delay));
}

function requestAwardTypes(user: AppUser, filter: any, canRetry = true): Promise<any[]> {
  const { roleSessionKey: key, activeSchoolId } = user;

  return axios
    .get(`${window.apiBase}/i/schools/${activeSchoolId}/awardTypes`, {
      headers: { usid: key },
      // Keep one transport format for this endpoint. In particular, this prevents
      // nested filters from being interpreted differently by proxy/query-parser versions.
      params: { filter: JSON.stringify(filter || {}) }
    })
    .then(response => response.data)
    .catch(error => {
      if (!canRetry || !isRetryableGatewayError(error)) {
        throw error;
      }

      return wait(GATEWAY_RETRY_DELAY).then(() => requestAwardTypes(user, filter, false));
    });
}

/**
 * Loads the requested range in bounded pages. Award artwork makes a single response
 * with thousands of rows unnecessarily heavy and can exceed the stage proxy timeout.
 */
export async function getAwardTypesList(user: AppUser, filter: any = {}): Promise<any[]> {
  const requestedLimit = Math.max(0, Number(filter.limit ?? DEFAULT_LIMIT));
  const initialSkip = Math.max(0, Number(filter.skip ?? DEFAULT_SKIP));
  const result: any[] = [];

  if (requestedLimit === 0) return result;

  while (result.length < requestedLimit) {
    const pageLimit = Math.min(AWARD_TYPES_PAGE_LIMIT, requestedLimit - result.length);
    const page = await requestAwardTypes(user, {
      ...filter,
      skip: initialSkip + result.length,
      limit: pageLimit
    });

    result.push(...page);
    if (page.length < pageLimit) break;
  }

  return result;
}

/** AWARD TYPE TABLE */
export function getAwardTypes(user: AppUser, filter = DEFAULT_FILTER) {
  return requestAwardTypes(user, filter);
}

export function getAllAwardTypes(user: AppUser, where?) {
  return getAwardTypesList(user, {
    ...(typeof where !== 'undefined' ? { where: { ...where } } : {}),
    limit: DEFAULT_LIMIT,
    skip: DEFAULT_SKIP
  });
}

export function getAwardTypesCount(user: AppUser, where?) {
  return getAllAwardTypes(user, where).then(items => ({ count: items.length }));
}

export function createAwardType(user: AppUser, data) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios.post(`${window.apiBase}/i/schools/${activeSchoolId}/awardTypes`, data, config).then(response => {
    return response.data;
  });
}

export function updateAwardType(user: AppUser, data, awardTypeId: string) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .put(`${window.apiBase}/i/schools/${activeSchoolId}/awardTypes/${awardTypeId}`, data, config)
    .then(response => {
      return response.data;
    });
}

export function deleteAwardType(user: AppUser, awardTypeId) {
  const { roleSessionKey: key, activeSchoolId } = user;

  const config = {
    headers: { usid: key }
  };

  return axios
    .delete(`${window.apiBase}/i/schools/${activeSchoolId}/awardTypes/${awardTypeId}`, config)
    .then(response => {
      return response.data;
    });
}
